import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  Search, Loader2, Clock, Calendar, MapPin, Award, CheckCircle2,
  ChevronRight, Mail, Phone, Building, Send, X,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const inputCls = "bg-[#14101c] border border-white/10 text-white placeholder:text-slate-500 focus:border-[#f7c935]";

// Public catalogue listing — no auth required
export default function CataloguePage() {
  const [programs, setPrograms] = useState([]);
  const [search, setSearch] = useState("");
  const [modality, setModality] = useState("");
  const [cpfOnly, setCpfOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (modality) params.set("modality", modality);
      if (cpfOnly) params.set("eligible_cpf", "true");
      const r = await axios.get(`${API}/api/catalogue/programs?${params.toString()}`);
      setPrograms(r.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }, [search, modality, cpfOnly]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-[#0b0811] text-white" data-testid="public-catalogue-page">
      {/* Hero */}
      <header className="bg-gradient-to-br from-[#14101c] to-[#0b0811] border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="text-sm font-bold uppercase tracking-widest text-[#f7c935] mb-3">Catalogue public</div>
          <h1 className="text-5xl md:text-6xl font-black tracking-tighter max-w-3xl">Trouvez votre prochaine formation.</h1>
          <p className="text-slate-400 mt-3 text-lg max-w-2xl">Programmes certifiants, distanciel et presentiel. Eligibles CPF et finance OPCO.</p>
          <div className="mt-8 flex flex-wrap gap-3 items-center bg-[#0b0811] border border-white/10 rounded-xl p-3 max-w-3xl">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher un programme, certification..." className={`${inputCls} pl-9`} data-testid="catalogue-search" />
            </div>
            <select value={modality} onChange={e => setModality(e.target.value)} className={`${inputCls} px-3 py-2 rounded text-sm`} data-testid="catalogue-modality">
              <option value="">Toutes modalites</option>
              <option value="presentiel">Presentiel</option>
              <option value="distanciel">Distanciel</option>
              <option value="mixte">Mixte</option>
              <option value="elearning">E-learning</option>
            </select>
            <label className="flex items-center gap-2 text-sm cursor-pointer px-3 py-2 bg-[#14101c] rounded">
              <input type="checkbox" checked={cpfOnly} onChange={e => setCpfOnly(e.target.checked)} data-testid="catalogue-cpf" />
              Eligible CPF
            </label>
          </div>
        </div>
      </header>

      {/* Grid */}
      <section className="max-w-6xl mx-auto px-6 py-10">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="catalogue-loading">
            {[0,1,2,3,4,5].map(i => (
              <div key={i} className="bg-[#14101c] border border-white/10 rounded-2xl p-5 animate-pulse">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 rounded-full bg-white/15" />
                  <div className="h-3 w-20 bg-white/10 rounded" />
                </div>
                <div className="h-5 w-3/4 bg-white/15 rounded mb-2" />
                <div className="h-3 w-full bg-white/10 rounded mb-1" />
                <div className="h-3 w-2/3 bg-white/10 rounded mb-4" />
                <div className="flex gap-3">
                  <div className="h-3 w-16 bg-white/10 rounded" />
                  <div className="h-3 w-20 bg-white/10 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) :
          programs.length === 0 ? (
            <div className="text-center py-20 text-slate-500">Aucun programme disponible pour votre recherche.</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {programs.map(p => (
                <button
                  key={p.program_id}
                  onClick={() => setSelected(p)}
                  data-testid={`catalogue-card-${p.program_id}`}
                  className="group text-left bg-[#14101c] border border-white/10 rounded-2xl p-5 hover:border-[#f7c935]/50 transition-all hover:-translate-y-1"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-2 h-2 rounded-full" style={{ background: p.color || "#f59e0b" }} />
                    {p.eligible_cpf && <span className="text-[9px] px-2 py-0.5 rounded bg-blue-500/20 text-blue-300 font-bold">CPF</span>}
                    <span className="text-[9px] px-2 py-0.5 rounded bg-white/5 text-slate-400">{p.modality}</span>
                  </div>
                  <h3 className="text-lg font-bold group-hover:text-[#f7c935] transition-colors">{p.title}</h3>
                  {p.sous_titre && <div className="text-xs text-[#f7c935]/80 mt-1">{p.sous_titre}</div>}
                  <p className="text-sm text-slate-400 mt-3 line-clamp-3">{p.description || p.section_description}</p>
                  <div className="flex items-center gap-3 mt-4 text-xs text-slate-500">
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{p.duration_hours || 0}h</span>
                    {(p.prix_entreprise || p.price_ht) > 0 && <span>{p.prix_entreprise || p.price_ht} € HT</span>}
                  </div>
                </button>
              ))}
            </div>
          )}
      </section>

      {selected && <ProgramInfoDialog program={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

// ─── Program detail + contact form ─────────────────────────
function ProgramInfoDialog({ program, onClose }) {
  const [detail, setDetail] = useState(null);
  const [showContact, setShowContact] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/catalogue/programs/${program.program_id}`)
      .then(r => setDetail(r.data)).catch(() => toast.error("Erreur"));
  }, [program.program_id]);

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl bg-[#0b0811] border border-white/10 text-white max-h-[90vh] overflow-y-auto" data-testid={`program-dialog-${program.program_id}`}>
        <DialogHeader>
          <DialogTitle className="text-2xl">{program.title}</DialogTitle>
        </DialogHeader>
        {!detail ? <Loader2 className="w-6 h-6 animate-spin text-[#f7c935] mx-auto my-10" /> : (
          <div className="space-y-4">
            {detail.sous_titre && <div className="text-sm text-[#f7c935]">{detail.sous_titre}</div>}
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="bg-white/5 px-2 py-0.5 rounded">{detail.modality}</span>
              {detail.eligible_cpf && <span className="bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded">CPF</span>}
              <span className="bg-white/5 px-2 py-0.5 rounded flex items-center gap-1"><Clock className="w-3 h-3" />{detail.duration_hours}h / {detail.duration_days}j</span>
              {detail.certification && <span className="bg-[#f7c935]/10 text-[#f7c935] px-2 py-0.5 rounded flex items-center gap-1"><Award className="w-3 h-3" />{detail.certification}</span>}
            </div>

            {(detail.description || detail.section_description) && (
              <section>
                <h3 className="text-xs uppercase font-bold text-slate-400 mb-2">Description</h3>
                <p className="text-sm text-slate-300 whitespace-pre-wrap">{detail.description || detail.section_description}</p>
              </section>
            )}

            {Array.isArray(detail.section_objectifs_pedagogiques) && detail.section_objectifs_pedagogiques.length > 0 && (
              <section>
                <h3 className="text-xs uppercase font-bold text-slate-400 mb-2">Objectifs pedagogiques</h3>
                <div className="space-y-1 border-l-2 border-[#f7c935]/30 pl-3">
                  {detail.section_objectifs_pedagogiques.map((o, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm"><CheckCircle2 className="w-4 h-4 text-[#f7c935] flex-shrink-0 mt-0.5" />{o}</div>
                  ))}
                </div>
              </section>
            )}

            {detail.section_profil_apprenants && (
              <section>
                <h3 className="text-xs uppercase font-bold text-slate-400 mb-2">Public cible</h3>
                <p className="text-sm text-slate-300">{detail.section_profil_apprenants}</p>
              </section>
            )}

            <section className="grid md:grid-cols-3 gap-3">
              <div className="bg-[#14101c] border border-white/10 rounded-xl p-4">
                <div className="text-xs text-slate-400 uppercase font-bold mb-1">Entreprise</div>
                <div className="text-xl font-black">{detail.prix_entreprise || detail.price_ht || 0} €<span className="text-xs text-slate-500"> HT</span></div>
              </div>
              <div className="bg-[#14101c] border border-white/10 rounded-xl p-4">
                <div className="text-xs text-slate-400 uppercase font-bold mb-1">Particulier</div>
                <div className="text-xl font-black">{detail.prix_particulier || detail.price_ht || 0} €<span className="text-xs text-slate-500"> HT</span></div>
              </div>
              <div className="bg-[#14101c] border border-white/10 rounded-xl p-4">
                <div className="text-xs text-slate-400 uppercase font-bold mb-1">Independant</div>
                <div className="text-xl font-black">{detail.prix_independant || detail.price_ht || 0} €<span className="text-xs text-slate-500"> HT</span></div>
              </div>
            </section>

            {detail.upcoming_sessions?.length > 0 && (
              <section>
                <h3 className="text-xs uppercase font-bold text-slate-400 mb-2">Prochaines sessions</h3>
                <div className="space-y-1">
                  {detail.upcoming_sessions.map(s => (
                    <div key={s.session_id} className="bg-[#14101c] border border-white/10 rounded p-3 text-sm flex items-center justify-between">
                      <div><Calendar className="w-3 h-3 inline text-[#f7c935]" /> {s.start_date} → {s.end_date}</div>
                      <div className="text-xs text-slate-400 flex items-center gap-1"><MapPin className="w-3 h-3" />{s.location || s.modality}</div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <Button onClick={() => setShowContact(true)} className="bg-[#f7c935] text-[#14101c] font-bold hover:bg-[#ffd948] w-full" data-testid="catalogue-request-info-btn">
              <Mail className="w-4 h-4 mr-2" /> Demander des informations
            </Button>
          </div>
        )}
        {showContact && <ContactFormDialog program={program} onClose={() => { setShowContact(false); onClose(); }} />}
      </DialogContent>
    </Dialog>
  );
}

function ContactFormDialog({ program, onClose }) {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", company: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (!form.first_name || !form.email) { toast.error("Prenom et email requis"); return; }
    setSending(true);
    try {
      await axios.post(`${API}/api/catalogue/request-info`, { program_id: program.program_id, ...form });
      setDone(true);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setSending(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md bg-[#0b0811] border border-white/10 text-white" data-testid="catalogue-contact-form">
        <DialogHeader><DialogTitle>Demander des infos — {program.title}</DialogTitle></DialogHeader>
        {done ? (
          <div className="text-center py-8 space-y-3">
            <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto" />
            <div className="font-bold">Demande envoyee</div>
            <p className="text-sm text-slate-400">L'equipe pedagogique vous recontactera sous peu.</p>
            <Button onClick={onClose} className="bg-white/5">Fermer</Button>
          </div>
        ) : (
          <>
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Prenom *" value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} className={inputCls} data-testid="contact-firstname" />
                <Input placeholder="Nom" value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} className={inputCls} />
              </div>
              <Input type="email" placeholder="Email *" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="contact-email" />
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Telephone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className={inputCls} />
                <Input placeholder="Entreprise" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} className={inputCls} />
              </div>
              <Textarea placeholder="Votre message" value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} rows={3} className={inputCls} />
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <Button variant="ghost" onClick={onClose}>Annuler</Button>
              <Button onClick={submit} disabled={sending || !form.email || !form.first_name} className="bg-[#f7c935] text-[#14101c] font-bold" data-testid="contact-submit">
                {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Envoyer
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
